import * as z from "zod";
import { InvoiceDataSchema } from "../invoice-data";

/**
 * Creates a base order edit input schema with a typed data field
 * @param dataSchema - The Zod schema for the operation-specific data
 */
export function createOrderEditInputSchema<TData extends z.ZodTypeAny>(
  dataSchema: TData,
) {
  return z.object({
    orderIdOrOrderNoOrConfirmationNo: z
      .string()
      .describe("The order number, ID, or confirmation number"),
    customerEmail: z
      .string()
      .describe("Customer's email address for verification"),
    data: dataSchema,
  });
}

/**
 * Creates a base order edit output schema with typed data and metadata fields
 * @param dataSchema - The Zod schema for the operation-specific result data
 * @param metadataSchema - Optional Zod schema for additional metadata fields
 */
export function createOrderEditOutputSchema<
  TData extends z.ZodTypeAny,
  TMetadata extends z.ZodTypeAny = z.ZodTypeAny,
>(dataSchema: TData, metadataSchema?: TMetadata) {
  const baseSchema = {
    success: z.boolean().describe("Whether the operation was successful"),
    orderId: z.string().describe("The order ID that was edited"),
    error: z.string().optional().describe("Error message if operation failed"),
    data: dataSchema.optional().describe("Operation-specific result data"),
    invoiceData: InvoiceDataSchema.optional().describe(
      "Invoice data if payment is required",
    ),
  };

  // If metadata schema is provided, add it as a nested metadata field
  if (metadataSchema) {
    return z.object({
      ...baseSchema,
      metadata: metadataSchema.optional().describe("Additional metadata"),
    });
  }

  return z.object(baseSchema);
}

/**
 * Base input type that all order editing operations follow
 */
export interface BaseOrderEditInput<TData = unknown> {
  orderIdOrOrderNoOrConfirmationNo: string;
  customerEmail: string;
  data: TData;
}

/**
 * Base output type that all order editing operations follow
 */
export interface BaseOrderEditOutput<TData = unknown, TMetadata = unknown> {
  success: boolean;
  orderId: string;
  error?: string;
  data?: TData;
  invoiceData?: z.infer<typeof InvoiceDataSchema>;
  metadata?: TMetadata;
}
